import React from "react";
import "./listingVitals.css";
import { formatAddress } from "../../lib/Contact/util";
import { IListing, IListingSummary } from "../../lib/Listing";
import { BiMap } from "react-icons/bi";
import { IconContext } from "react-icons";
import { IVitalIconsProps, VitalsIcons } from "./VitalsIcons";
import { FaTag } from "react-icons/fa";

export enum EListingVitalsLayout {
  row1Border0 = "row1Border0",
  column1Border0 = "column1Border0",
  column1Border1 = "column1Border1",
  column1Border1Alt1 = "column1Border1Alt1",
  column1Border1Alt2 = "column1Border1Alt2",
  column2Border1 = "column2Border1",
  column2Border2 = "column2Border2",
  column2Border0 = "column2Border0",
  column2Column1Border1 = "column2Column1Border1",
  column1VitalsRows = "column1VitalsRows",
}

export interface IListingVitalsProps {
  layout?: EListingVitalsLayout;
  composite?: boolean;
  locationIcon?: boolean;
  className?: string;
  listing: IListing;
  includeVitalsIcons?: boolean;
  omitAddressParts?: ("city" | "state" | "postal")[];
  twoLineAddress?: boolean;
  cityName?: boolean;
  priceClassName?: string;
  locationClassName?: string;
  vitalIconsClassName?: string;
  showClosedTag?: boolean;
  showCourtesy?: boolean;
  replaceIconsForText?: boolean;
  includeMinorArea?: boolean;
  showFlags?: boolean;
  vitalIconsProps?: Partial<IVitalIconsProps>;
  customAddressFormat?: (listing: IListingSummary) => any;
  priceTagIcon?: boolean;
}

export const ListingVitals: React.FC<IListingVitalsProps> = ({
  listing,
  className,
  composite = false,
  includeVitalsIcons = true,
  layout = EListingVitalsLayout.column1Border0,
  locationIcon = true,
  omitAddressParts = [],
  twoLineAddress = false,
  cityName = false,
  priceClassName = "",
  locationClassName = "",
  vitalIconsClassName = "",
  showClosedTag = false,
  showCourtesy = false,
  replaceIconsForText = false,
  includeMinorArea = false,
  showFlags = false,
  vitalIconsProps,
  customAddressFormat,
  priceTagIcon = false,
}) => {
  const tcClass = composite ? "text-composite-text" : undefined;
  const bcClass = composite ? undefined : "border-divider-inverted";

  let layoutVariant = {
    containerStyle: {},
    containerClasses: "",
    price: "",
    location: "",
    vitals: "",
  };

  switch (layout) {
    case EListingVitalsLayout.row1Border0:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"price location vitals"',
          justifyContent: "center",
        },
        containerClasses: "text-sm mt-2 gap-x-2",
        price: "text-sm",
        location: `text-sm ${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        vitals: "text-sm",
      };
      break;
    case EListingVitalsLayout.column1Border0:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"price" "location" "vitals"',
        },
        containerClasses: "p-3",
        location: `${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        price: "text-lg sm:text-2xl",
      };
      break;
    case EListingVitalsLayout.column1Border1:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"price" "location" "vitals"',
        },
        containerClasses: "p-3",
        price: "pb-2",
        location: `pb-2 ${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        vitals: "border-t pt-2",
      };
      break;
    case EListingVitalsLayout.column1Border1Alt1:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"location" "price" "vitals"',
        },
        containerClasses: "p-3",
        price: "pb-2 text-3xl md:text-4xl lg:text-5xl border-t",
        location: `pb-2 whitespace-pre-line`,
        vitals: "pt-2",
      };
      break;
    case EListingVitalsLayout.column1Border1Alt2:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"location" "price" "vitals"',
        },
        containerClasses: "p-3",
        price: "pb-2 text-3xl md:text-4xl lg:text-5xl border-b",
        location: `pb-5 whitespace-pre-line`,
        vitals: "pt-2 justify-center",
      };
      break;
    case EListingVitalsLayout.column2Border1:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          alignItems: "center",
          justifyContent: "center",
          gridTemplateAreas: '"price location" "price vitals" ',
        },
        containerClasses: "p-3",
        price: "text-lg sm:text-2xl pr-3",
        location: `border-l pl-2 ${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        vitals: "border-l pl-2",
      };
      break;
    case EListingVitalsLayout.column2Border2:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          alignItems: "center",
          justifyContent: "center",
          gridTemplateAreas: '"price location" "price vitals" ',
        },
        containerClasses: "p-3",
        price: "text-lg sm:text-5xl pr-3",
        location: `border-l pb-1 pl-2 ${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        vitals: "border-l border-t pt-1 pl-2",
      };
      break;
    case EListingVitalsLayout.column2Border0:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          alignItems: "center",
          justifyContent: "center",
          gridTemplateAreas: '"price location" "price vitals" ',
        },
        containerClasses: "p-3",
        price: "text-4xl pr-3",
        location: `pb-1 pl-2 ${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        vitals: "pt-1 pl-2",
      };
      break;
    case EListingVitalsLayout.column2Column1Border1:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          alignItems: "center",
          justifyContent: "center",
          justifyItems: "center",
          gridTemplateAreas: '"price vitals" "location location" ',
        },
        containerClasses: "p-3",
        location: `${twoLineAddress ? "whitespace-pre-line" : "whitespace-nowrap"
          }`,
        price: "text-lg sm:text-4xl pr-3",
        vitals: "border-l pl-3",
      };
      break;
    case EListingVitalsLayout.column1VitalsRows:
      layoutVariant = {
        ...layoutVariant,
        containerStyle: {
          gridTemplateAreas: '"location" "price" "vitals"',
        },
        containerClasses: "text-right",
        price: "flex justify-end gap-x-5 text-base [&>*:first-child]:order-last",
        vitals: "!flex-col [&>div]:flex [&>div]:justify-end [&>div>*:first-child]:ml-5 [&>div>*:first-child]:order-last"
      }
  }

  const getListingMessage = (listing: IListing) => {
    const { status } = listing;

    const upperCaseStatus = status?.toUpperCase();

    return upperCaseStatus == "PENDING"
      ? "UNDER CONTRACT"
      : upperCaseStatus == "CONTINGENT"
        ? "ACCEPTING BACKUPS"
        : upperCaseStatus == "CLOSED"
          ? "ASKING PRICE"
          : listing.isJustListed
            ? "JUST LISTED"
            : listing.isPriceReduced
              ? "PRICE REDUCED"
              : "";
  };

  return (
    <IconContext.Provider
      value={{ size: "1.5em", className: "mr-1 inline align-bottom" }}
    >
      <div
        className={`listing-vitals grid ${layoutVariant.containerClasses} ${tcClass} ${className}`}
        style={layoutVariant.containerStyle}
      >
        {showFlags && (
          <div className="flex flex-row flex-wrap items-end md:justify-end w-full">
            <div className={"vitals-flags font-light mr-2"}>
              {getListingMessage(listing)}
            </div>
            {listing.price != null &&
              listing.price != undefined &&
              listing.price > 0 && (
                <div
                  className={`font-bold ${layoutVariant.price} ${priceClassName}`}
                  style={{ gridArea: "price" }}
                >
                  ${new Intl.NumberFormat().format(listing.price)}
                  {listing.priceSuffix}
                </div>
              )}
          </div>
        )}
        {!showFlags &&
          listing.price != null &&
          listing.price != undefined &&
          listing.price > 0 && (
            <div
              className={`font-bold ${layoutVariant.price} ${priceClassName}`}
              style={{ gridArea: "price" }}
            >
              {priceTagIcon && !replaceIconsForText && <FaTag />}
              ${new Intl.NumberFormat().format(listing.price)}
              {listing.priceSuffix}
            </div>
          )
        }
        {listing.price != null &&
          listing.price != undefined &&
          listing.price == 0 && (
            <div
              className={`font-bold ${layoutVariant.price} ${priceClassName}`}
              style={{ gridArea: "price" }}
            >
              Price Upon Request
            </div>
          )}
        {!listing.price && (listing.priceLow || listing.priceHigh) && (
          <div
            className={`font-bold ${layoutVariant.price} ${priceClassName}`}
            style={{ gridArea: "price" }}
          >
            {listing.priceLow &&
              `$${new Intl.NumberFormat().format(listing.priceLow)}`}
            {listing.priceHigh && listing.priceLow && ` - `}
            {listing.priceHigh &&
              `$${new Intl.NumberFormat().format(listing.priceHigh)}`}
            {listing.priceSuffix}
          </div>
        )}
        {includeVitalsIcons && (
          <div
            className={`vitals-icons flex gap-3 ${bcClass} ${layoutVariant.vitals} ${vitalIconsClassName}`}
            style={{ gridArea: "vitals" }}
          >
            <VitalsIcons
              listing={listing}
              replaceIconsForText={replaceIconsForText}
              {...vitalIconsProps}
            />
          </div>
        )}
        <div
          className={`overflow-hidden text-ellipsis ${bcClass} ${layoutVariant.location} ${locationClassName}`}
          style={{ gridArea: "location" }}
        >
          {locationIcon && <BiMap className="mr-0.5 -ml-0.5" />}
          {!customAddressFormat && formatAddress(
            listing.address,
            twoLineAddress,
            omitAddressParts,
            includeMinorArea ? listing.minorArea : null
          )}
          {customAddressFormat && customAddressFormat(listing)}
        </div>
        {listing.status == "Closed" && showClosedTag && (
          <div className={`text-4xl p-3`}>Listing Sold</div>
        )}
        {showCourtesy && listing.officeName && (
          <div className="flex flex-wrap justify-center listing-features gap-4 text-xs py-3 italic whitespace-pre">
            <span>
              Listing Courtesy of{"\n"}
              <span className="font-bold">{listing.officeName}</span>
            </span>
          </div>
        )}
      </div>
    </IconContext.Provider>
  );
};
